import { Link } from 'react-router-dom';
import CommonHeader from '../../components/feature/CommonHeader';
import CommonFooter from '../../components/feature/CommonFooter';
import FAQSection from '../../components/feature/FAQSection';
import Breadcrumb from '../../components/feature/Breadcrumb';
import indexSource from '../../data/related/index.json';
import theButler from '../../data/related/the-butler.json';
import butlerAssociation from '../../data/related/butler-association.json';
import butlerAcademy from '../../data/related/butler-academy.json';
import queensButler from '../../data/related/queens-butler.json';

/**
 * 関連サイト(一覧 + 詳細4本) — 2026-09-18 作り直し v2(Yuichi 差し戻し対応)。
 * 作りの正典 = THE BUTLER の RelatedSiteDetailPage.tsx / relatedSites.ts / RelatedSitesPage.tsx。
 * ブロック種別・画像の大きさ・順序を THE BUTLER と 1:1 で揃える(配色・書体・部品は執事ベル)。
 *  - ビルボード: 全幅・実画像・min-height 78vh(SP 62vh)・暗幕 → パンくず → 公式CTA①
 *  - 章帯 + 節: twocol(画像 50%・4:3)/ cards(3 or 4・4:3)/ tiers(段階行・サムネ)/ official(帯)
 *  - 公式CTA②: 中間 / FAQ(faq.json・FAQSection)/ 他の関連サイト(3 枚カード)/ 最終CTA帯
 * 本文・画像・FAQ は src/data/related/*.json(1 サイト = 1 ファイル、JA/EN 併記)。
 * 画像はすべて紹介される側リポの src が参照している実画像の無加工コピー(src/data/related/image-ledger.json)。
 */

type Locale = 'ja' | 'en';
type Media = { src: string; alt: string; altEn: string };
type Sub = { t: string; tEn: string; b: string; bEn: string };
type TwoCol = { kind: 'twocol'; band: string; bandEn: string; tag: string; tagEn: string; h: string; hEn: string; body: string[]; bodyEn: string[]; subs?: Sub[]; subheads?: Record<string, string>; subheadsEn?: Record<string, string>; image: Media; reverse?: boolean; tint?: boolean };
type Card = { folio: string; title: string; titleEn: string; desc: string; descEn: string; image: Media };
type Cards = { kind: 'cards'; band: string; bandEn: string; lede: string; ledeEn: string; tint?: boolean; cards: Card[] };
type Tier = { lv: string; title: string; titleEn: string; tag: string; tagEn: string; desc: string; descEn: string; image: Media };
type Tiers = { kind: 'tiers'; band: string; bandEn: string; lede: string; ledeEn: string; tint?: boolean; tiers: Tier[] };
type Official = { kind: 'official'; band: string; bandEn: string; body: string; bodyEn: string; label: string; labelEn: string };
type Band = { kind: 'band'; band: string; bandEn: string; h: string; hEn: string; lede: string; ledeEn: string; points: { t: string; tEn: string; b: string; bEn: string }[] };
type Quote = { kind: 'quote'; band: string; bandEn: string; h: string; hEn: string; q: string; qEn: string; by: string; byEn: string };
type Block = TwoCol | Cards | Tiers | Official | Band | Quote;
type Hero = Media & { kicker: string; kickerEn: string; title: string; titleEn: string; lead: string; leadEn: string };
type Faq = { q: string; qEn: string; a: string; aEn: string };
type DeeperLink = { label: string; labelEn: string; href: string };
type SiteSlug = 'the-butler' | 'butler-association' | 'butler-academy' | 'queens-butler';
type Site = {
  slug: SiteSlug;
  name: { ja: string; en: string };
  tagline: string;
  taglineEn: string;
  officialUrl: string;
  hero: Hero;
  cardImage: Media;
  cardLead?: string;
  cardLeadEn?: string;
  blocks: Block[];
  faq: Faq[];
  deeper: DeeperLink[];
};
type IndexData = {
  hero: Hero;
  intro: { kicker: string; kickerEn: string; h1: string; h1En: string; lede: string; ledeEn: string; body: string[]; bodyEn: string[] };
  connections: { band: string; bandEn: string; lede: string; ledeEn: string; image: Media; items: { slug: SiteSlug; t: string; tEn: string; b: string; bEn: string; image?: Media }[] };
  scenes?: { band: string; bandEn: string; lede: string; ledeEn: string; cards: { t: string; tEn: string; b: string; bEn: string; image: Media }[] };
  articles: { band: string; bandEn: string; lede: string; ledeEn: string; links: DeeperLink[] };
  faq: Faq[];
};

const sites = [theButler, butlerAssociation, butlerAcademy, queensButler] as unknown as Site[];
const index = indexSource as unknown as IndexData;
const SLUGS: SiteSlug[] = ['the-butler', 'butler-association', 'butler-academy', 'queens-butler'];
export const RELATED_OFFICIAL_URL: Record<SiteSlug, string> = Object.fromEntries(sites.map((s) => [s.slug, s.officialUrl])) as Record<SiteSlug, string>;

const alt = (m: Media, l: Locale) => (l === 'ja' ? m.alt : m.altEn);
const wrap = 'mx-auto max-w-6xl px-5 sm:px-6 lg:px-8';
const wide = 'mx-auto max-w-7xl px-5 sm:px-6 lg:px-8';
const btnBase = 'inline-flex items-center justify-center transition-all duration-300 text-center';
const btn = (kind: 'solid' | 'ghost' | 'line') =>
  kind === 'solid'
    ? `${btnBase} bg-[#C8A96A] px-11 py-4 text-white tracking-[.2em] hover:bg-[#b8965a]`
    : kind === 'ghost'
      ? `${btnBase} border border-[#c8a96aa6] px-12 py-[18px] text-white tracking-[.22em] hover:bg-[#c8a96a2e]`
      : `${btnBase} border border-[#C8A96A] px-10 py-3.5 tracking-[.16em] text-[#171714] hover:bg-[#C8A96A] hover:text-white`;

function ExternalCta({ site, kind, locale }: { site: Site; kind: 'solid' | 'ghost' | 'line'; locale: Locale }) {
  return (
    <a href={site.officialUrl} target="_blank" rel="noopener noreferrer" className={btn(kind)} data-pattern={kind === 'solid' ? 'BTN-05' : kind === 'ghost' ? 'BTN-09' : 'BTN-01'}>
      {kind !== 'solid'
        ? locale === 'ja'
          ? '公式サイトへ進む'
          : 'Official site'
        : locale === 'ja'
          ? `${site.name.ja}の公式サイトへ進む`
          : `Official site of ${site.name.en}`}
    </a>
  );
}

/** 章帯(番号 + 章名)。THE BUTLER の ChapterBand に相当。節ごとに 1 本。 */
function ChapterBand({ no, name, sub }: { no: string; name: string; sub: string }) {
  return (
    <div className="flex items-center gap-4 bg-[#171714] px-5 py-3 text-xs tracking-[.24em] text-[#d7b779] sm:px-8" data-pattern="HD-BAND">
      <span className="font-serif text-base tracking-normal text-[#C8A96A]">{no}</span>
      <span className="h-px w-8 bg-[#C8A96A]" aria-hidden="true" />
      <span className="text-[13px] tracking-[.18em] text-white">{name}</span>
      <span className="ml-auto hidden uppercase sm:inline">{sub}</span>
    </div>
  );
}

function Billboard({ hero, locale, kickerFallback, children }: { hero: Hero; locale: Locale; kickerFallback: string; children?: React.ReactNode }) {
  return (
    <section className="relative isolate flex min-h-[62vh] items-end overflow-hidden bg-[#171714] md:min-h-[78vh]" data-pattern="BB-01">
      <img src={hero.src} alt={alt(hero, locale)} className="absolute inset-0 h-full w-full object-cover object-[center_32%] brightness-[.5]" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0c0e]/80 via-[#0a0c0e]/35 to-transparent" aria-hidden="true" />
      <div className={`${wide} relative z-10 w-full pb-12 pt-16 text-white md:pb-16`}>
        <span className="text-xs uppercase tracking-[.3em] text-[#d7b779]">{locale === 'ja' ? hero.kicker : hero.kickerEn || kickerFallback}</span>
        <h1 className="mt-3 font-serif text-3xl leading-[1.22] tracking-[.05em] md:text-5xl">{locale === 'ja' ? hero.title : hero.titleEn}</h1>
        <p className="mt-5 max-w-[42em] text-[15px] leading-[1.9] text-white/90 md:text-[17px]">{locale === 'ja' ? hero.lead : hero.leadEn}</p>
        {children}
      </div>
    </section>
  );
}

function TwoColBlock({ b, locale }: { b: TwoCol; locale: Locale }) {
  const body = locale === 'ja' ? b.body : b.bodyEn;
  return (
    <section className={`py-14 md:py-20 ${b.tint ? 'bg-[#faf8f5]' : 'bg-white'}`}>
      <div className={wide}>
        <span className="block text-xs uppercase tracking-[.3em] text-[#b8965a]">{locale === 'ja' ? b.tag : b.tagEn}</span>
        <div className={`mt-4 grid items-start gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)] md:gap-14 ${b.reverse ? 'md:[&>*:first-child]:order-2' : ''}`}>
          <div>
            <h2 className="font-serif text-2xl leading-snug text-[#171714] md:text-[34px]">{locale === 'ja' ? b.h : b.hEn}</h2>
            <div className="mt-4 mb-5 h-px w-[70px] bg-[#C8A96A]" aria-hidden="true" />
            {body.map((p, i) => {
              const sh = (locale === 'ja' ? b.subheads : b.subheadsEn)?.[String(i)];
              return (
                <div key={i}>
                  {sh && (
                    <h3 className="mt-7 font-serif text-lg leading-snug text-[#171714]">
                      <span className="mr-2 text-[#C8A96A]" aria-hidden="true">—</span>{sh}
                    </h3>
                  )}
                  <p className="mt-3 max-w-[46em] leading-[1.9] text-gray-700">{p}</p>
                </div>
              );
            })}
            {b.subs?.map((s, i) => (
              <div key={i} className="mt-5">
                <h3 className="font-serif text-lg text-[#171714]">{locale === 'ja' ? s.t : s.tEn}</h3>
                <p className="mt-1 max-w-[46em] leading-[1.85] text-gray-700">{locale === 'ja' ? s.b : s.bEn}</p>
              </div>
            ))}
          </div>
          <div className="overflow-hidden md:sticky md:top-16">
            <img src={b.image.src} alt={alt(b.image, locale)} loading="lazy" className="aspect-[4/3] w-full object-cover md:aspect-[4/5]" />
          </div>
        </div>
      </div>
    </section>
  );
}

function CardsBlock({ b, locale }: { b: Cards; locale: Locale }) {
  const cols = b.cards.length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-3';
  return (
    <section className={`py-12 md:py-16 ${b.tint ? 'bg-[#faf8f5]' : 'bg-white'}`}>
      <div className={wide}>
        <p className="max-w-[52em] leading-[1.9] text-gray-700">{locale === 'ja' ? b.lede : b.ledeEn}</p>
        <div className={`mt-8 grid gap-6 ${cols}`}>
          {b.cards.map((c) => (
            <article key={c.folio} className="flex flex-col overflow-hidden border border-[#d9d1c2] bg-white">
              <div className="overflow-hidden">
                <img src={c.image.src} alt={alt(c.image, locale)} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.06]" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="font-serif text-sm italic text-[#b8965a]">{c.folio}</span>
                <h3 className="mt-1 font-serif text-xl leading-snug text-[#171714]">{locale === 'ja' ? c.title : c.titleEn}</h3>
                <p className="mt-3 leading-[1.8] text-gray-600">{locale === 'ja' ? c.desc : c.descEn}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuoteBlock({ b, locale }: { b: Quote; locale: Locale }) {
  return (
    <section className="bg-[#f4efe6] py-14 md:py-20">
      <div className={wrap}>
        <h3 className="text-xs uppercase tracking-[.3em] text-[#a8874c]">{locale === 'ja' ? b.h : b.hEn}</h3>
        <blockquote className="mt-6 border-l-2 border-[#C8A96A] pl-6 font-serif text-xl leading-[1.85] text-[#171714] md:pl-9 md:text-[26px]">
          {locale === 'ja' ? b.q : b.qEn}
        </blockquote>
        <div className="mt-5 pl-6 text-sm tracking-[.08em] text-[#7a6a4f] md:pl-9">{locale === 'ja' ? b.by : b.byEn}</div>
      </div>
    </section>
  );
}

function BandBlock({ b, locale }: { b: Band; locale: Locale }) {
  return (
    <section className="bg-[#171714] py-14 text-white md:py-20">
      <div className={wide}>
        <h2 className="font-serif text-2xl leading-snug text-[#e9dcc0] md:text-[30px]">{locale === 'ja' ? b.h : b.hEn}</h2>
        <div className="mt-4 h-px w-[70px] bg-[#C8A96A]" aria-hidden="true" />
        <p className="mt-5 max-w-[54em] leading-[1.95] text-[#d9d3c6]">{locale === 'ja' ? b.lede : b.ledeEn}</p>
        <div className="mt-9 grid gap-6 md:grid-cols-3">
          {b.points.map((pt, i) => (
            <div key={pt.t} className="border-t border-[#C8A96A] pt-5">
              <span className="font-serif text-sm italic text-[#C8A96A]">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="mt-1 font-serif text-lg text-white">{locale === 'ja' ? pt.t : pt.tEn}</h3>
              <p className="mt-2 leading-[1.85] text-[#c9c3b6]">{locale === 'ja' ? pt.b : pt.bEn}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TiersBlock({ b, locale }: { b: Tiers; locale: Locale }) {
  return (
    <section className={`py-12 md:py-16 ${b.tint ? 'bg-[#faf8f5]' : 'bg-white'}`}>
      <div className={wrap}>
        <p className="max-w-[52em] leading-[1.9] text-gray-700">{locale === 'ja' ? b.lede : b.ledeEn}</p>
        <ol className="mt-8 space-y-4">
          {b.tiers.map((t) => (
            <li key={t.lv} className="flex items-center gap-5 border border-[#d9d1c2] bg-white p-4 transition-transform duration-300 hover:translate-x-1.5 hover:border-[#C8A96A] sm:p-5">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#C8A96A]/10 font-serif text-[15px] italic text-[#b8965a]" aria-hidden="true">{t.lv}</span>
              <div className="hidden h-[104px] w-[104px] flex-shrink-0 overflow-hidden sm:block md:h-[150px] md:w-[200px]">
                <img src={t.image.src} alt={alt(t.image, locale)} loading="lazy" className="h-full w-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-serif text-lg text-[#171714]">{locale === 'ja' ? t.title : t.titleEn}</h3>
                <span className="block text-xs tracking-[.12em] text-gray-500">{locale === 'ja' ? t.tag : t.tagEn}</span>
                <p className="mt-2 leading-[1.8] text-gray-600">{locale === 'ja' ? t.desc : t.descEn}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function OfficialBlock({ b, site, locale }: { b: Official; site: Site; locale: Locale }) {
  return (
    <section className="bg-white py-14 text-center md:py-16">
      <div className={wrap}>
        <p className="mx-auto max-w-[44em] leading-[1.9] text-gray-700">{locale === 'ja' ? b.body : b.bodyEn}</p>
        <div className="mt-8"><a href={site.officialUrl} target="_blank" rel="noopener noreferrer" className={btn('solid')} data-pattern="BTN-05">{locale === 'ja' ? b.label : b.labelEn}</a></div>
      </div>
    </section>
  );
}

function Block({ b, site, locale }: { b: Block; site: Site; locale: Locale }) {
  if (b.kind === 'twocol') return <TwoColBlock b={b} locale={locale} />;
  if (b.kind === 'cards') return <CardsBlock b={b} locale={locale} />;
  if (b.kind === 'tiers') return <TiersBlock b={b} locale={locale} />;
  if (b.kind === 'band') return <BandBlock b={b} locale={locale} />;
  if (b.kind === 'quote') return <QuoteBlock b={b} locale={locale} />;
  return <OfficialBlock b={b} site={site} locale={locale} />;
}

function OtherSites({ current, locale }: { current?: SiteSlug; locale: Locale }) {
  const prefix = locale === 'en' ? '/en' : '';
  const others = sites.filter((s) => s.slug !== current);
  return (
    <section className="bg-white py-14 md:py-16">
      <div className={wide}>
        <ChapterBand no="" name={locale === 'ja' ? '他の関連サイト' : 'Other Related Sites'} sub="Related" />
        <div className={`mt-8 grid gap-6 md:grid-cols-2 ${others.length === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
          {others.map((s) => (
            <Link key={s.slug} to={`${prefix}/related/${s.slug}`} className="group flex flex-col overflow-hidden border border-[#d9d1c2] bg-white transition-colors hover:border-[#C8A96A]">
              <div className="overflow-hidden">
                <img src={s.cardImage.src} alt={alt(s.cardImage, locale)} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-serif text-xl text-[#171714]">{s.name[locale]}</h3>
                <p className="mt-2 leading-[1.8] text-gray-600">{locale === 'ja' ? s.tagline : s.taglineEn}</p>
                <span className="mt-auto pt-4 text-sm text-[#A26A1D]">{locale === 'ja' ? '詳しく見る' : 'Learn more'} →</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactBand({ locale }: { locale: Locale }) {
  const prefix = locale === 'en' ? '/en' : '';
  return (
    <section className="bg-[#171714] px-5 py-14 text-center text-white md:py-16">
      <h2 className="font-serif text-2xl md:text-[28px]">{locale === 'ja' ? '執事について、お気軽にご相談ください' : 'Feel free to ask about butlers'}</h2>
      <p className="mx-auto mt-3 max-w-[32em] leading-[1.85] text-gray-300">{locale === 'ja' ? '各事業の窓口の見分け方や、どこへ相談すべきか迷われたときは、執事ベルからお取り次ぎいたします。' : 'If you are unsure which desk to approach, Butler Bell will point you to the right one.'}</p>
      <div className="mt-7"><Link to={`${prefix}/contact`} className={btn('solid')} data-pattern="BTN-05">{locale === 'ja' ? 'お問い合わせ' : 'Contact'}</Link></div>
    </section>
  );
}

export function RelatedLP({ slug, locale }: { slug: SiteSlug; locale: Locale }) {
  const site = sites.find((s) => s.slug === slug);
  if (!site) return null;
  const prefix = locale === 'en' ? '/en' : '';
  const midIdx = Math.floor(site.blocks.length / 2);
  let n = 0;
  const no = () => String(++n).padStart(2, '0');
  return (
    <div className="min-h-screen bg-white" lang={locale}>
      <CommonHeader />
      <div className="pt-[132px]">
        <Billboard hero={site.hero} locale={locale} kickerFallback={`Related Site / ${site.name.en}`}>
          <div className="mt-8"><ExternalCta site={site} kind="ghost" locale={locale} /></div>
        </Billboard>
      </div>
      <Breadcrumb items={[{ name: locale === 'ja' ? '関連サイト' : 'Related Sites', href: `${prefix}/related` }, { name: site.name[locale] }]} />
      <main>
        <div className="flex justify-center py-8"><ExternalCta site={site} kind="line" locale={locale} /></div>
        {site.blocks.map((b, i) => (
          <div key={i}>
            <ChapterBand no={no()} name={locale === 'ja' ? b.band : b.bandEn} sub={b.bandEn} />
            <Block b={b} site={site} locale={locale} />
            {i === midIdx && (
              <div className="flex justify-center bg-white pb-6"><ExternalCta site={site} kind="line" locale={locale} /></div>
            )}
          </div>
        ))}
        <ChapterBand no="" name={locale === 'ja' ? 'よくあるご質問' : 'FAQ'} sub="FAQ" />
        <FAQSection />
        <OtherSites current={site.slug} locale={locale} />
        {site.deeper.length > 0 && (
          <section className={`${wide} pb-14`}>
            <h2 className="border-b border-[#C8A96A] pb-3 font-serif text-xl text-[#171714]">{locale === 'ja' ? '執事ベルの記事で、さらに詳しく' : "Read further in Butler Bell's own articles"}</h2>
            <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {site.deeper.map((d) => (
                <li key={d.href}><Link to={`${prefix}${d.href}`} className="text-[#A26A1D] underline-offset-4 hover:underline">{locale === 'ja' ? d.label : d.labelEn} →</Link></li>
              ))}
            </ul>
          </section>
        )}
        <ContactBand locale={locale} />
      </main>
      <CommonFooter />
    </div>
  );
}

export function RelatedIndex({ locale }: { locale: Locale }) {
  const prefix = locale === 'en' ? '/en' : '';
  const d = index;
  return (
    <div className="min-h-screen bg-white" lang={locale}>
      <CommonHeader />
      <div className="pt-[132px]">
        <Billboard hero={d.hero} locale={locale} kickerFallback="Related Sites" />
      </div>
      <Breadcrumb items={[{ name: locale === 'ja' ? '関連サイト' : 'Related Sites' }]} />
      <main>
        <section className="bg-white py-14 md:py-20">
          <div className={`${wrap} text-center`}>
            <span className="text-xs uppercase tracking-[.3em] text-[#b8965a]">{locale === 'ja' ? d.intro.kicker : d.intro.kickerEn}</span>
            <h2 className="mt-3 font-serif text-2xl leading-snug text-[#171714] md:text-4xl">{locale === 'ja' ? d.intro.h1 : d.intro.h1En}</h2>
            <div className="mx-auto mt-5 h-px w-[70px] bg-[#C8A96A]" aria-hidden="true" />
            <p className="mx-auto mt-6 max-w-[42em] leading-[1.9] text-gray-700">{locale === 'ja' ? d.intro.lede : d.intro.ledeEn}</p>
            {(locale === 'ja' ? d.intro.body : d.intro.bodyEn).map((p, i) => (
              <p key={i} className="mx-auto mt-4 max-w-[42em] text-left leading-[1.9] text-gray-700">{p}</p>
            ))}
          </div>
        </section>
        <ChapterBand no="01" name={locale === 'ja' ? '四つの関連事業' : 'Four Related Bodies'} sub="Four Bodies" />
        <section className="bg-white py-12 md:py-16">
          <div className={wide}>
            <div className="grid gap-7 md:grid-cols-2">
              {sites.map((s) => (
                <Link key={s.slug} to={`${prefix}/related/${s.slug}`} className="group flex flex-col overflow-hidden border border-[#d9d1c2] bg-white transition-colors hover:border-[#C8A96A]">
                  <div className="overflow-hidden">
                    <img src={s.cardImage.src} alt={alt(s.cardImage, locale)} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-serif text-2xl text-[#171714]">{s.name[locale]}</h3>
                    <p className="mt-3 leading-[1.85] text-gray-700">{locale === 'ja' ? (s.cardLead ?? s.tagline) : (s.cardLeadEn ?? s.taglineEn)}</p>
                    <span className="mt-auto pt-5 text-sm tracking-[.06em] text-[#A26A1D]">{locale === 'ja' ? '詳しく見る' : 'Learn more'} →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
        <ChapterBand no="02" name={locale === 'ja' ? d.connections.band : d.connections.bandEn} sub={d.connections.bandEn} />
        <section className="bg-[#faf8f5] py-12 md:py-16">
          <div className={wrap}>
            <div className="grid items-start gap-8 md:grid-cols-2 md:gap-11">
              <div>
                <p className="leading-[1.9] text-gray-700">{locale === 'ja' ? d.connections.lede : d.connections.ledeEn}</p>
                <div className="mt-6 space-y-5">
                  {d.connections.items.map((c) => (
                    <div key={c.slug} className="flex gap-4 border-l-4 border-[#C8A96A] pl-4">
                      {c.image && (
                        <img src={c.image.src} alt={alt(c.image, locale)} loading="lazy" className="hidden aspect-[4/3] w-[132px] flex-shrink-0 object-cover sm:block" />
                      )}
                      <div className="min-w-0">
                        <h3 className="font-serif text-lg text-[#171714]">{locale === 'ja' ? c.t : c.tEn}</h3>
                        <p className="mt-1 leading-[1.8] text-gray-700">{locale === 'ja' ? c.b : c.bEn}</p>
                        <Link to={`${prefix}/related/${c.slug}`} className="mt-1 inline-block text-sm text-[#A26A1D]">{locale === 'ja' ? '詳細ページへ' : 'Read the detail page'} →</Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <img src={d.connections.image.src} alt={alt(d.connections.image, locale)} loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </div>
        </section>
        {d.scenes && (
          <>
            <ChapterBand no="03" name={locale === 'ja' ? d.scenes.band : d.scenes.bandEn} sub={d.scenes.bandEn} />
            <section className="bg-white py-12 md:py-16">
              <div className={wide}>
                <p className="max-w-[52em] leading-[1.9] text-gray-700">{locale === 'ja' ? d.scenes.lede : d.scenes.ledeEn}</p>
                <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {d.scenes.cards.map((c) => (
                    <article key={c.image.src} className="flex flex-col overflow-hidden border border-[#d9d1c2] bg-white">
                      <img src={c.image.src} alt={alt(c.image, locale)} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="font-serif text-lg leading-snug text-[#171714]">{locale === 'ja' ? c.t : c.tEn}</h3>
                        <p className="mt-2 leading-[1.8] text-gray-600">{locale === 'ja' ? c.b : c.bEn}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
        <ChapterBand no="" name={locale === 'ja' ? 'よくあるご質問' : 'FAQ'} sub="FAQ" />
        <FAQSection />
        <section className="bg-white py-10">
          <div className={wide}>
            <h2 className="border-b border-[#C8A96A] pb-3 font-serif text-xl text-[#171714]">{locale === 'ja' ? d.articles.band : d.articles.bandEn}</h2>
            <p className="mt-3 text-sm leading-[1.8] text-gray-600">{locale === 'ja' ? d.articles.lede : d.articles.ledeEn}</p>
            <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {d.articles.links.map((l) => (
                <li key={l.href}><Link to={`${prefix}${l.href}`} className="text-[#A26A1D] underline-offset-4 hover:underline">{locale === 'ja' ? l.label : l.labelEn} →</Link></li>
              ))}
            </ul>
          </div>
        </section>
        <ContactBand locale={locale} />
      </main>
      <CommonFooter />
    </div>
  );
}

export { SLUGS };
